import { useCallback, useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useDb, useEnsureAuth } from '../firebase/FirebaseContext';
import {
  parsePublishedGuide, parsePublishedGuideIndex, PUBLISHED_GUIDES_COLLECTION,
  PUBLISHED_GUIDE_INDEX_COLLECTION, PUBLISHED_GUIDE_INDEX_ID,
} from './publishedGuides';

export type DocumentState<T> =
  | { status: 'loading' | 'missing'; data?: undefined }
  | { status: 'error'; data?: undefined }
  | { status: 'ready'; data: T };

/**
 * The ONE read path for a server-owned, strictly-parsed singleton document:
 * ensure auth, subscribe, parse, and turn a parse throw into `status: 'error'`
 * rather than a render crash.
 *
 * Exported (THE-582) so the gift-guide hub's own published docs — trending and
 * the occasion mapping — read through this exact function instead of a second
 * near-copy of it. A new server-owned doc should never have to re-decide what
 * "missing" or "malformed" means.
 *
 * `requireServerForMissing` (THE-1393, `usePublishedGuide` only) tells this to
 * distrust a "no document" answer that came from Firestore's local cache
 * (`snapshot.metadata.fromCache`) rather than the server: on a dropped/slow
 * connection the SDK answers "no such document" from whatever it has cached,
 * and that is not proof the document was removed, only that this client has
 * nothing cached for it yet. With the flag on, such a snapshot is dropped —
 * the caller stays in whatever state it was already in (`loading`, or a
 * previously-`ready` doc) until a snapshot that is NOT `fromCache` settles
 * the question either way. Every other reader (the guide index, hub docs,
 * rails) has no load-time budget of its own, so treating their cache misses
 * as "still loading" could leave them loading forever — they keep the
 * flag off and today's immediate "missing" behaviour.
 *
 * Listening with `{ includeMetadataChanges: true }` is required for the flag
 * to work at all: with the SDK's default listener options, a snapshot whose
 * only change is metadata (e.g. a cache "no document" followed by the server
 * confirming the exact same "no document") is suppressed outright, so the
 * server's confirmation would never reach this hook.
 */
export function usePublishedDocument<T>(
  collection: string,
  id: string,
  parse: (value: unknown, id: string) => T,
  skip = false,
  requireServerForMissing = false,
) {
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{ id: string; state: DocumentState<T> }>({ id, state: { status: 'loading' } });
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  useEffect(() => {
    if (skip) return;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    // The raw (pre-parse) payload behind the last `ready` state, as JSON, so a
    // later re-emit of IDENTICAL data (a metadata-only cache catch-up to what
    // the server already confirmed) can be recognised and skipped — handing
    // consumers the same `data` object instead of a new one. Both GuidePage
    // and the dive-deeper overlay reload their product fan-out whenever that
    // object's identity changes, so a no-op re-emit must not look like an
    // edit.
    let lastReadyJson: string | undefined;
    const update = (state: DocumentState<T>) => { if (!cancelled) setResult({ id, state }); };
    update({ status: 'loading' });
    const handleSnapshot = (snapshot: {
      exists: () => boolean;
      data: () => unknown;
      metadata?: { fromCache: boolean };
    }) => {
      try {
        if (!snapshot.exists()) {
          if (requireServerForMissing && snapshot.metadata?.fromCache) return;
          lastReadyJson = undefined;
          update({ status: 'missing' });
          return;
        }
        const rawData = snapshot.data();
        if (requireServerForMissing) {
          const rawJson = JSON.stringify(rawData);
          if (rawJson === lastReadyJson) return;
          lastReadyJson = rawJson;
        }
        update({ status: 'ready', data: parse(rawData, id) });
      } catch {
        update({ status: 'error' });
      }
    };
    void ensureAuth().then(() => {
      if (cancelled) return;
      const ref = doc(db, collection, id);
      const onError = () => update({ status: 'error' });
      unsubscribe = requireServerForMissing
        ? onSnapshot(ref, { includeMetadataChanges: true }, handleSnapshot, onError)
        : onSnapshot(ref, handleSnapshot, onError);
    }).catch(() => update({ status: 'error' }));
    return () => { cancelled = true; unsubscribe?.(); };
  }, [db, ensureAuth, collection, id, parse, attempt, skip, requireServerForMissing]);
  const state: DocumentState<T> = result.id === id ? result.state : { status: 'loading' };
  return { ...state, retry };
}

export function usePublishedGuide(slug: string) {
  return usePublishedDocument(PUBLISHED_GUIDES_COLLECTION, slug, parsePublishedGuide, false, true);
}

export function usePublishedGuideIndex(skip = false) {
  return usePublishedDocument(PUBLISHED_GUIDE_INDEX_COLLECTION, PUBLISHED_GUIDE_INDEX_ID, parsePublishedGuideIndex, skip);
}
